import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Mail, TrendingUp, TrendingDown, AlertCircle, CheckCircle2, Users,
  Target, ArrowLeft, Loader2, Minus,
} from 'lucide-react';
import axios from 'axios';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DigestPreview() {
  const navigate = useNavigate();
  const [digest, setDigest] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [dRes, sRes] = await Promise.all([
          axios.get(`${API}/digest/preview`, { withCredentials: true }),
          axios.get(`${API}/digest/settings`, { withCredentials: true }),
        ]);
        if (dRes.data?.has_business) setDigest(dRes.data);
        setSettings(sRes.data);
      } catch (e) {
        if (e.response?.status === 401) navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const toggleSetting = async (key) => {
    const updated = { ...settings, [key]: !settings[key] };
    setSettings(updated);
    try {
      await axios.put(`${API}/digest/settings`, { [key]: !settings[key] }, { withCredentials: true });
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4C9B8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!digest) {
    return (
      <AppShell active="home">
        <div className="p-5 text-center pt-20">
          <p className="text-sm text-[#5C5347]">Start monitoring first to preview your digest.</p>
        </div>
      </AppShell>
    );
  }

  const scoreChange = digest.score?.change || 0;

  return (
    <AppShell active="home">
      <div className="p-5 max-w-2xl mx-auto pb-28" data-testid="digest-preview-page">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-xs text-[#5C5347] hover:text-[#3C3226] mb-3 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft size={14} /> Back
        </button>

        <div className="flex items-center gap-2 mb-1">
          <Mail size={18} className="text-[#EF9F27]" />
          <h1 className="font-display text-2xl font-medium text-[#3C3226] tracking-tight">Weekly Digest Preview</h1>
        </div>
        <p className="text-sm text-[#5C5347] mb-4">
          This is what your weekly email would look like. Sending activates once SendGrid is connected.
        </p>

        {/* Digest settings */}
        {settings && (
          <motion.div
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="p-4 rounded-none bg-[#F7F4EF] border border-[#D4C9B8] mb-4"
            data-testid="digest-settings"
          >
            <p className="text-xs font-bold uppercase tracking-wider text-[#5C5347] mb-3">Digest Preferences</p>
            <div className="space-y-2">
              {[
                { key: 'include_score', label: 'Score changes' },
                { key: 'include_alerts', label: 'Alert summaries' },
                { key: 'include_competitors', label: 'Competitor updates' },
                { key: 'include_actions', label: 'Recommended actions' },
              ].map(item => (
                <label key={item.key} className="flex items-center justify-between cursor-pointer">
                  <span className="text-sm text-[#3C3226]">{item.label}</span>
                  <button
                    onClick={() => toggleSetting(item.key)}
                    className={`w-9 h-5 rounded-none transition-colors relative ${
                      settings[item.key] ? 'bg-[#3C3226]' : 'bg-[#D4C9B8]'
                    }`}
                    data-testid={`toggle-${item.key}`}
                  >
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-[#F7F4EF] shadow transition-transform ${
                      settings[item.key] ? 'translate-x-[18px]' : 'translate-x-0.5'
                    }`} />
                  </button>
                </label>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-[#D4C9B8] flex items-center gap-2">
              <span className="text-[10px] text-[#5C5347]">Status:</span>
              <span className="text-[10px] font-bold text-[#EF9F27] bg-[#EF9F27]/10 px-2 py-0.5 rounded-full">
                SendGrid not configured
              </span>
            </div>
          </motion.div>
        )}

        {/* Digest email preview */}
        <motion.div
          initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="rounded-none border border-[#D4C9B8] overflow-hidden bg-[#F7F4EF] shadow-sm"
          data-testid="digest-email-preview"
        >
          {/* Email header */}
          <div className="bg-[#3C3226] p-5 text-center">
            <p className="font-display text-lg text-[#F0EBE3] font-medium tracking-tight">GetChosenFirst</p>
            <p className="text-[11px] text-[#F0EBE3]/60 mt-0.5">Weekly AI Recommendations Report</p>
          </div>

          {/* Email body */}
          <div className="p-5 space-y-4">
            <div>
              <p className="text-sm text-[#3C3226]">Hi{digest.business_name ? ` ${digest.business_name}` : ''},</p>
              <p className="text-xs text-[#5C5347] mt-1">Here's your week in review ({digest.period}).</p>
            </div>

            {/* Score section */}
            <div className="p-4 rounded-sm bg-[#F7F4EF] border border-[#D4C9B8]">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#5C5347] mb-1">AI Recommendation Score</p>
                  <p className="text-3xl font-bold text-[#3C3226]">{digest.score?.current || 0}</p>
                </div>
                <div className="text-right">
                  <div className={`flex items-center gap-1 ${scoreChange > 0 ? 'text-[#1D6B4F]' : scoreChange < 0 ? 'text-[#E24B4A]' : 'text-[#5C5347]'}`}>
                    {scoreChange > 0 ? <TrendingUp size={16} /> : scoreChange < 0 ? <TrendingDown size={16} /> : <Minus size={16} />}
                    <span className="text-lg font-bold">{scoreChange > 0 ? '+' : ''}{scoreChange}</span>
                  </div>
                  <p className="text-[10px] text-[#5C5347]">vs last week</p>
                </div>
              </div>
            </div>

            {/* Weekly highlights */}
            <div>
              <p className="text-xs font-bold uppercase tracking-wider text-[#5C5347] mb-2">This Week's Highlights</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="p-3 rounded-sm bg-[#1D6B4F]/5 border border-[#1D6B4F]/10 text-center">
                  <p className="text-lg font-bold text-[#1D6B4F]">{digest.highlights?.gaps_closed || 0}</p>
                  <p className="text-[10px] text-[#5C5347]">Gaps Closed</p>
                </div>
                <div className="p-3 rounded-sm bg-[#C67A5C]/5 border border-[#C67A5C]/10 text-center">
                  <p className="text-lg font-bold text-[#C67A5C]">{digest.highlights?.content_published || 0}</p>
                  <p className="text-[10px] text-[#5C5347]">Content Published</p>
                </div>
                <div className="p-3 rounded-sm bg-[#EF9F27]/5 border border-[#EF9F27]/10 text-center">
                  <p className="text-lg font-bold text-[#EF9F27]">{digest.highlights?.alerts_count || 0}</p>
                  <p className="text-[10px] text-[#5C5347]">New Alerts</p>
                </div>
                <div className="p-3 rounded-sm bg-[#886FBF]/5 border border-[#886FBF]/10 text-center">
                  <p className="text-lg font-bold text-[#886FBF]">{digest.highlights?.referral_partners || 0}</p>
                  <p className="text-[10px] text-[#5C5347]">Referral Partners</p>
                </div>
              </div>
            </div>

            {/* Alerts */}
            {digest.highlights?.alerts?.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#5C5347] mb-2">Recent Alerts</p>
                <div className="space-y-1.5">
                  {digest.highlights.alerts.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 rounded-none bg-[#F7F4EF]">
                      {a.severity === 'success' ? <CheckCircle2 size={12} className="text-[#1D6B4F]" /> :
                       a.severity === 'warning' ? <AlertCircle size={12} className="text-[#EF9F27]" /> :
                       <AlertCircle size={12} className="text-[#5C5347]" />}
                      <span className="text-xs text-[#3C3226]">{a.title}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Competitors */}
            {digest.competitors?.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#5C5347] mb-2">Competitor Watch</p>
                <div className="space-y-1.5">
                  {digest.competitors.map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded-none bg-[#F7F4EF]">
                      <span className="text-xs text-[#3C3226] font-medium">{c.name}</span>
                      <span className="text-xs font-bold text-[#5C5347]">{c.score}/100</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top actions */}
            {digest.top_actions?.length > 0 && (
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-[#5C5347] mb-2">Top 3 Actions This Week</p>
                <div className="space-y-1.5">
                  {digest.top_actions.map((action, i) => (
                    <div key={i} className="flex items-start gap-2 p-2 rounded-none bg-[#1D6B4F]/3">
                      <Target size={12} className="text-[#1D6B4F] mt-0.5 flex-shrink-0" />
                      <span className="text-xs text-[#3C3226]">{action}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="pt-3 border-t border-[#D4C9B8] text-center">
              <p className="text-[10px] text-[#5C5347]">
                This digest will be emailed weekly once SendGrid is configured.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </AppShell>
  );
}
